/**
 * The family's noticing engine — "{companion} · noticed something" on the
 * parent home (ui-ux-guideline §2.9 item 3, 2026-09-18). Pure: it reads what
 * the page fetched and returns each sentence as a message descriptor (#2109
 * R3-B3); the page resolves them and the callout is presentational.
 *
 * The lanes are the facts that are WAITING, and only those:
 *   1. an appointment within two days — the companion offers to draft
 *      questions (the old guidance card's one rule, word for word); its own
 *      action stays primary because nothing on the platform owns "drafting
 *      questions" but the conversation;
 *   2. to-dos that are overdue → Tasks;
 *   2b. receipts that need the family → the "Finish these receipts" section
 *      of the Grants page (#2973). The count is the one /bills says ("N
 *      receipts need you", `needsAttention` over every receipt, linked to a
 *      grant or not), so the two surfaces never disagree;
 *   3. questions TRF is asking — polls the family has not answered → Community,
 *      where the poll is answered;
 *   4. renewals, when the family's Signature grant is in its final 3 months
 *      (or past its end) and no renewal is in yet, or while a renewal draft is
 *      under way → the renewal (Victor, 2026-10-03; the rule is
 *      `signatureRenewalDue` in lib/grants-status, shared with /bills);
 *   5. TRF paid — the latest payment staff RECORDED on the family's grants,
 *      when its day is within the last 7 days → the Grants page (#2972). It
 *      is the one lane that is news rather than waiting, so it speaks last.
 *      It says only what the record says: TRF paid this amount on this day.
 * Nothing waits behind a medication or a symptom, so those are not lanes.
 *
 * The memorial register (#644, #693): the page treats an in-flight family
 * record as memorial, and in memorial only the polls lane speaks — no
 * treatment appointment, no to-do cheer, no invitation to renew a
 * treatment-support grant. The gate is a required argument on purpose: a
 * pure function cannot read the family record, and a default would let the
 * next caller opt out by staying silent.
 */
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { GrantCurrency, GrantRequest, MedicalAppointment, Task } from '@/types/api';
import type { RenewalCtaState } from '@/lib/renewal-display';
import { whenKeyForTask } from '@/lib/task-display';
import { formatList, formatMoney, formatMonthDay, formatWeekday } from '@/i18n/intl';
import { msg, type Msg } from '@/i18n/msg';

/**
 * One noticing sentence, as message descriptors (#2109 R3-B3). The page
 * resolves them at render (`useMsg`), so a language switch re-renders them;
 * `CompanionCallout` takes the resolved strings, which keeps the staff home's
 * `WilliamsCallout` on the same component unchanged.
 *
 * The prompt is written in the UI language too (R3-B2, ruled 2026-10-15): it
 * is what the parent sends.
 */
export interface ParentPattern {
  id: string;
  body: Msg;
  /** The primary label when the pattern has no `href`. */
  ctaLabel: Msg;
  /** The prompt handed to `onAsk`. */
  prompt: Msg;
  /** The page that owns the decision, with its filter set. */
  href?: string;
}

export interface ParentRenewalFacts {
  /** `signatureRenewalDue(grants)` once the grants fetch settled; false until then. */
  due: boolean;
  /** `deriveRenewalCta` / `renewalCtaFromError` once the fetch settled; null while pending. */
  state: RenewalCtaState | null;
  /** `renewalYearFromApp`, when known. */
  year: string | null;
  /**
   * #2527: what TRF asked about, as the family reads it (`askedItemNames` in
   * lib/flagged-fields), while the renewal is `needs_info`. Null or absent
   * when the names are not known yet: the sentence then says TRF needs
   * something without naming it.
   */
  asked?: readonly string[] | null;
}

export interface ParentNoticingInputs {
  appointments: MedicalAppointment[];
  /** Already through `visibleParentTasks` — the page's one task filter. */
  tasks: Task[];
  /**
   * Receipts waiting on the family — `needsAttention` over the claims fetch,
   * the count /bills shows (#2973); null while unknown (loading or failed).
   */
  receiptsNeedYou?: number | null;
  /** Polls the family has not answered; null while unknown (loading or failed). */
  unansweredPolls: number | null;
  renewal: ParentRenewalFacts;
  /**
   * The family's grants (`GET /grants`), for the TRF-paid lane (#2972); null
   * while unknown (loading or failed), which says nothing.
   */
  grants?: GrantRequest[] | null;
  companionName: string;
  isMemorial: boolean;
  now?: Date;
  /**
   * The locale the formatted param (the weekday) is written in. Omitted, it
   * follows the active UI language — the page puts the language in the memo
   * that calls this, so a switch rebuilds it.
   */
  locale?: string;
}

/**
 * An appointment the companion may describe as upcoming (#2411, #2433): a
 * scheduled visit, or a legacy row with no status (the model defaults new
 * rows to scheduled). A cancelled, attended or missed visit is not waiting,
 * so no noticing card on home, /tasks or /chat names it.
 */
export function isScheduledAppointment(a: Pick<MedicalAppointment, 'status'> | { status?: string }): boolean {
  return a.status == null || a.status === 'scheduled';
}

const OPEN_TASK_STATUSES: ReadonlySet<Task['status']> = new Set(['open', 'claimed', 'in_progress']);

/** The appointment sentence, one whole key per day and per known/unknown provider. */
const APPOINTMENT_BODY = {
  today: { provider: 'dashboard:noticing.appointment.today', doctor: 'dashboard:noticing.appointment.todayDoctor' },
  tomorrow: { provider: 'dashboard:noticing.appointment.tomorrow', doctor: 'dashboard:noticing.appointment.tomorrowDoctor' },
  onDay: { provider: 'dashboard:noticing.appointment.onDay', doctor: 'dashboard:noticing.appointment.onDayDoctor' },
} as const;

export interface RecentGrantPayment {
  amount_cents: number;
  /** 'YYYY-MM-DD', the day staff recorded it was paid. */
  paid_on: string;
  currency: GrantCurrency;
}

/** How many calendar days back a recorded payment is still news on the home (#2972). */
export const RECENT_PAYMENT_DAYS = 7;

/**
 * The latest payment staff recorded on the family's grants whose day is
 * within the last `RECENT_PAYMENT_DAYS` calendar days (today included), or
 * null. Reads `award.last_payment`, the parent projection of the payment
 * rows; a day in the future is not "paid" yet and is skipped.
 */
export function recentGrantPayment(
  grants: readonly GrantRequest[],
  now: Date = new Date(),
): RecentGrantPayment | null {
  let best: RecentGrantPayment | null = null;
  for (const g of grants) {
    const last = g.award?.last_payment;
    if (!last || !g.award) continue;
    const days = differenceInCalendarDays(now, parseISO(last.paid_on));
    if (!Number.isFinite(days) || days < 0 || days > RECENT_PAYMENT_DAYS) continue;
    if (!best || last.paid_on > best.paid_on) {
      best = { amount_cents: last.amount_cents, paid_on: last.paid_on, currency: g.award.currency };
    }
  }
  return best;
}

export function buildParentPatterns(input: ParentNoticingInputs): ParentPattern[] {
  const now = input.now ?? new Date();
  const out: ParentPattern[] = [];
  const companion = input.companionName;

  if (!input.isMemorial) {
    const upcoming = input.appointments
      .filter(isScheduledAppointment)
      .map((a) => ({ a, days: differenceInCalendarDays(parseISO(a.date_time), now) }))
      .filter(({ days }) => Number.isFinite(days) && days >= 0 && days <= 2)
      .sort((x, y) => new Date(x.a.date_time).getTime() - new Date(y.a.date_time).getTime())[0];
    if (upcoming) {
      const { a, days } = upcoming;
      const day = days === 0 ? 'today' : days === 1 ? 'tomorrow' : 'onDay';
      const provider = a.provider || '';
      const weekday = day === 'onDay'
        ? formatWeekday(a.date_time, { width: 'long', locale: input.locale })
        : undefined;
      out.push({
        id: `appointment-${a._id}`,
        body: msg(APPOINTMENT_BODY[day][provider ? 'provider' : 'doctor'], {
          title: a.title,
          ...(provider ? { provider } : {}),
          ...(weekday ? { weekday } : {}),
        }),
        ctaLabel: msg('dashboard:noticing.appointment.cta', { companion }),
        prompt: provider
          ? msg('dashboard:noticing.appointment.prompt', { provider, title: a.title })
          : msg('dashboard:noticing.appointment.promptDoctor', { title: a.title }),
      });
    }

    const overdue = input.tasks.filter(
      (t) => OPEN_TASK_STATUSES.has(t.status) && whenKeyForTask(t, now) === 'overdue',
    ).length;
    if (overdue > 0) {
      out.push({
        id: 'tasks-overdue',
        body: msg('dashboard:noticing.overdue.body', { count: overdue }),
        ctaLabel: msg('companion:callout.takeMeThere'),
        prompt: msg('dashboard:noticing.overdue.prompt', { count: overdue }),
        href: '/tasks',
      });
    }

    // #2973: the receipts TRF is waiting on, straight to the list of them.
    const receipts = input.receiptsNeedYou;
    if (receipts != null && receipts > 0) {
      out.push({
        id: 'receipts-todo',
        body: msg('dashboard:noticing.receipts.body', { count: receipts }),
        ctaLabel: msg('companion:callout.takeMeThere'),
        prompt: msg('dashboard:noticing.receipts.prompt', { count: receipts }),
        href: '/bills#receipts-todo',
      });
    }
  }

  if (input.unansweredPolls != null && input.unansweredPolls > 0) {
    const n = input.unansweredPolls;
    out.push({
      id: 'polls-unanswered',
      body: msg('dashboard:noticing.polls.body', { count: n }),
      ctaLabel: msg('companion:callout.takeMeThere'),
      prompt: msg('dashboard:noticing.polls.prompt'),
      href: '/community',
    });
  }

  if (!input.isMemorial && input.renewal.state) {
    const { state, due, year, asked } = input.renewal;
    // #2527: TRF sent the renewal back. Say so, and name what was asked —
    // "Your renewal is underway" told the family nothing was wanted from them.
    if (state.kind === 'needs_info') {
      out.push({
        id: 'renewal',
        body: asked && asked.length > 0
          ? msg('dashboard:noticing.renewal.needsInfoItems', {
            items: formatList(asked, { type: 'conjunction', locale: input.locale }),
          })
          : msg('dashboard:noticing.renewal.needsInfo'),
        ctaLabel: msg('companion:callout.takeMeThere'),
        prompt: msg('dashboard:noticing.renewal.prompt'),
        href: '/renew',
      });
    } else if (state.kind === 'editable' || (state.kind === 'none' && due)) {
      // A draft under way is always offered back; a fresh start only when due.
      // `pending` — already in; `unknown` — the fetch failed, and an invitation
      // is a claim only a resolved fetch may make (AP-58).
      const body = state.kind === 'none'
        ? msg('dashboard:noticing.renewal.due')
        : year
          ? msg('dashboard:noticing.renewal.inProgressYear', { year })
          : msg('dashboard:noticing.renewal.inProgress');
      out.push({
        id: 'renewal',
        body,
        ctaLabel: msg('companion:callout.takeMeThere'),
        prompt: msg('dashboard:noticing.renewal.prompt'),
        href: '/renew',
      });
    }
  }

  // #2972: what staff recorded, and only that — the amount and the day of the
  // latest payment in the last week. Memorial keeps its one lane (polls).
  const paid = !input.isMemorial && input.grants ? recentGrantPayment(input.grants, now) : null;
  if (paid) {
    out.push({
      id: 'grant-paid',
      body: msg('dashboard:noticing.paid.body', {
        amount: formatMoney(paid.amount_cents, paid.currency, { locale: input.locale }),
        date: formatMonthDay(paid.paid_on, { month: 'long', dateOnly: true, locale: input.locale }),
      }),
      ctaLabel: msg('companion:callout.takeMeThere'),
      prompt: msg('dashboard:noticing.paid.prompt'),
      href: '/bills',
    });
  }

  return out;
}
